import { useState, useEffect, useCallback } from 'react';
import { Link } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import { heroSlides, featuredComics, latestUpdates, categories, rankings, siteStats } from '@/mocks/comics';

function HeroSection() {
  const [currentSlide, setCurrentSlide] = useState(0);

  const nextSlide = useCallback(() => {
    setCurrentSlide((prev) => (prev + 1) % heroSlides.length);
  }, []);

  useEffect(() => {
    const timer = setInterval(nextSlide, 5000);
    return () => clearInterval(timer);
  }, [nextSlide]);

  const slide = heroSlides[currentSlide];

  return (
    <section className="relative h-[620px] md:h-[720px] w-full overflow-hidden">
      <div className="absolute inset-0 transition-opacity duration-700">
        <img
          src={slide.image}
          alt={slide.title}
          className="w-full h-full object-cover object-top"
        />
      </div>
      <div className="absolute inset-0 bg-gradient-to-b from-black/50 via-black/30 to-black/60" />

      <Navbar />

      <div className="relative z-10 flex flex-col items-center justify-center h-full text-center px-4">
        <span className="inline-block px-4 py-1.5 rounded-full bg-white/15 backdrop-blur-sm text-white/90 text-xs font-medium mb-6 whitespace-nowrap">
          {slide.category}
        </span>
        <h1 className="font-heading text-4xl md:text-6xl lg:text-7xl font-bold text-white mb-4 max-w-3xl leading-tight">
          {slide.title}
        </h1>
        <p className="text-white/80 text-base md:text-lg mb-8 max-w-xl">
          {slide.subtitle}
        </p>
        <div className="flex items-center gap-4">
          <Link
            to={`/article/${slide.id}`}
            className="whitespace-nowrap px-8 py-3 rounded-full bg-primary-500 text-white font-medium text-sm hover:bg-primary-600 transition-all cursor-pointer"
          >
            立即阅读
          </Link>
          <button className="whitespace-nowrap px-8 py-3 rounded-full bg-white/15 backdrop-blur-sm text-white font-medium text-sm hover:bg-white/25 transition-all cursor-pointer">
            了解更多
          </button>
        </div>

        <div className="absolute bottom-8 left-1/2 -translate-x-1/2 flex items-center gap-2">
          {heroSlides.map((_, index) => (
            <button
              key={index}
              onClick={() => setCurrentSlide(index)}
              className={`w-2 h-2 rounded-full transition-all cursor-pointer ${
                index === currentSlide
                  ? 'bg-white w-6'
                  : 'bg-white/40 hover:bg-white/60'
              }`}
              aria-label={`第 ${index + 1} 张`}
            />
          ))}
        </div>
      </div>
    </section>
  );
}

function FeaturedSection() {
  return (
    <section className="py-16 md:py-20 px-4 md:px-6">
      <div className="max-w-7xl mx-auto">
        <div className="text-center mb-12">
          <h2 className="font-heading text-3xl md:text-4xl font-bold text-foreground-900 mb-3">
            热门推荐
          </h2>
          <p className="text-foreground-500 text-sm md:text-base max-w-lg mx-auto">
            精选最受欢迎的韩漫作品，每一部都值得从头追到尾
          </p>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
          {featuredComics.map((comic) => (
            <Link
              key={comic.id}
              to={`/article/${comic.id}`}
              className="group relative rounded-xl overflow-hidden aspect-[3/4] cursor-pointer"
            >
              <img
                src={comic.cover}
                alt={comic.title}
                className="w-full h-full object-cover object-top transition-transform duration-500 group-hover:scale-105"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/20 to-transparent" />
              <div className="absolute bottom-0 left-0 right-0 p-5">
                <span className="inline-block px-2.5 py-0.5 rounded-full bg-primary-500/90 text-white text-xs mb-2 whitespace-nowrap">
                  {comic.category}
                </span>
                <h3 className="font-heading text-xl font-semibold text-white mb-1">
                  {comic.title}
                </h3>
                <p className="text-white/70 text-xs mb-3 line-clamp-2">
                  {comic.description}
                </p>
                <div className="flex items-center gap-3 text-white/60 text-xs">
                  <span>{comic.author}</span>
                  <span className="text-white/30">|</span>
                  <span>{comic.chapters}话</span>
                  <span className="text-white/30">|</span>
                  <span className="flex items-center gap-1">
                    <i className="ri-star-fill text-accent-400 text-xs"></i>
                    {comic.rating}
                  </span>
                </div>
              </div>
            </Link>
          ))}
        </div>
      </div>
    </section>
  );
}

function CategoriesSection() {
  return (
    <section className="py-16 md:py-20 px-4 md:px-6 bg-background-100">
      <div className="max-w-7xl mx-auto">
        <div className="text-center mb-12">
          <h2 className="font-heading text-3xl md:text-4xl font-bold text-foreground-900 mb-3">
            分类浏览
          </h2>
          <p className="text-foreground-500 text-sm md:text-base max-w-lg mx-auto">
            按喜好筛选，找到最适合你的韩漫类型
          </p>
        </div>

        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-4">
          {categories.map((cat) => (
            <Link
              key={cat.id}
              to="/category/all"
              className="group bg-background-50 rounded-xl p-5 flex flex-col items-center text-center transition-all duration-200 hover:bg-primary-50 cursor-pointer"
            >
              <div className="w-12 h-12 flex items-center justify-center rounded-full bg-accent-100 text-accent-600 mb-3 group-hover:bg-accent-200 transition-colors">
                <i className={`${cat.icon} text-xl`}></i>
              </div>
              <h3 className="font-medium text-foreground-900 text-sm mb-1 whitespace-nowrap">
                {cat.name}
              </h3>
              <span className="text-xs text-foreground-400">{cat.count}部</span>
            </Link>
          ))}
        </div>
      </div>
    </section>
  );
}

function LatestAndRankingSection() {
  return (
    <section id="latest" className="py-16 md:py-20 px-4 md:px-6">
      <div className="max-w-7xl mx-auto">
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-10">
          <div className="lg:col-span-2">
            <div className="flex items-center justify-between mb-8">
              <h2 className="font-heading text-2xl md:text-3xl font-bold text-foreground-900">
                最新更新
              </h2>
              <Link
                to="/category/all"
                className="text-sm text-primary-600 hover:text-primary-700 font-medium whitespace-nowrap"
              >
                查看更多 <i className="ri-arrow-right-line text-xs ml-1"></i>
              </Link>
            </div>

            <div className="grid grid-cols-2 sm:grid-cols-3 gap-4">
              {latestUpdates.map((comic) => (
                <Link
                  key={comic.id}
                  to={`/article/${comic.id}`}
                  className="group cursor-pointer"
                >
                  <div className="relative rounded-lg overflow-hidden aspect-[3/4] mb-3">
                    <img
                      src={comic.cover}
                      alt={comic.title}
                      className="w-full h-full object-cover object-top transition-transform duration-300 group-hover:scale-105"
                    />
                    <div className="absolute top-2 left-2 px-2 py-0.5 rounded-md bg-primary-500 text-white text-[10px] font-medium whitespace-nowrap">
                      NEW
                    </div>
                  </div>
                  <h4 className="text-sm font-medium text-foreground-900 truncate">
                    {comic.title}
                  </h4>
                  <p className="text-xs text-foreground-500 mt-1 truncate">
                    {comic.latestChapter}
                  </p>
                  <span className="text-[10px] text-foreground-400">
                    {comic.updateTime}
                  </span>
                </Link>
              ))}
            </div>
          </div>

          <div id="rankings">
            <div className="flex items-center justify-between mb-8">
              <h2 className="font-heading text-2xl md:text-3xl font-bold text-foreground-900">
                热门排行
              </h2>
              <Link
                to="/category/all"
                className="text-sm text-primary-600 hover:text-primary-700 font-medium whitespace-nowrap"
              >
                完整榜单 <i className="ri-arrow-right-line text-xs ml-1"></i>
              </Link>
            </div>

            <div className="bg-background-100 rounded-xl p-5">
              {rankings.map((item, index) => (
                <Link
                  key={item.rank}
                  to={`/article/${item.rank}`}
                  className={`flex items-center gap-4 py-3.5 cursor-pointer transition-colors hover:bg-background-50 rounded-lg px-2 -mx-2 ${
                    index < rankings.length - 1 ? 'border-b border-background-200/70' : ''
                  }`}
                >
                  <span
                    className={`w-7 h-7 flex items-center justify-center rounded-full text-xs font-bold flex-shrink-0 ${
                      item.rank <= 3
                        ? 'bg-primary-500 text-white'
                        : 'bg-secondary-200 text-foreground-500'
                    }`}
                  >
                    {item.rank}
                  </span>
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-medium text-foreground-900 truncate">
                      {item.title}
                    </p>
                    <p className="text-xs text-foreground-400 mt-0.5">
                      热度 {item.heat}
                    </p>
                  </div>
                  <span className="text-xs">
                    {item.trend === 'up' && <i className="ri-arrow-up-line text-green-500"></i>}
                    {item.trend === 'down' && <i className="ri-arrow-down-line text-red-400"></i>}
                    {item.trend === 'stable' && <i className="ri-subtract-line text-foreground-300"></i>}
                  </span>
                </Link>
              ))}
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}

function StatsSection() {
  return (
    <section className="py-14 px-4 md:px-6 bg-background-100">
      <div className="max-w-7xl mx-auto">
        <div className="grid grid-cols-2 md:grid-cols-4 gap-6">
          {[
            { value: siteStats.totalComics, label: '漫画总量', icon: 'ri-book-open-line' },
            { value: siteStats.dailyVisitors, label: '日访问量', icon: 'ri-user-line' },
            { value: siteStats.totalChapters, label: '章节总数', icon: 'ri-file-text-line' },
            { value: siteStats.registeredUsers, label: '注册用户', icon: 'ri-group-line' },
          ].map((stat) => (
            <div key={stat.label} className="text-center">
              <div className="w-12 h-12 mx-auto flex items-center justify-center rounded-full bg-accent-100 text-accent-600 mb-3">
                <i className={`${stat.icon} text-lg`}></i>
              </div>
              <p className="font-heading text-2xl md:text-3xl font-bold text-foreground-900">
                {stat.value}
              </p>
              <p className="text-xs text-foreground-500 mt-1">{stat.label}</p>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

function CTASection() {
  return (
    <section className="relative py-20 md:py-28 px-4 md:px-6 overflow-hidden">
      <div className="absolute inset-0">
        <img
          src="https://readdy.ai/api/search-image?query=A%20soft%20dreamy%20illustration%20with%20warm%20peach%20and%20cream%20gradient%2C%20floating%20sparkles%20of%20light%2C%20gentle%20bokeh%20effect%2C%20ethereal%20atmosphere%2C%20subtle%20anime-inspired%20aesthetic%2C%20clean%20minimal%20composition%2C%20warm%20cozy%20feeling&width=1600&height=600&seq=cta-bg-01&orientation=landscape"
          alt=""
          className="w-full h-full object-cover object-top"
        />
        <div className="absolute inset-0 bg-black/40" />
      </div>
      <div className="relative z-10 text-center max-w-2xl mx-auto">
        <h2 className="font-heading text-3xl md:text-4xl font-bold text-white mb-4">
          开启你的韩漫阅读之旅
        </h2>
        <p className="text-white/80 text-sm md:text-base mb-8">
          海量热门韩漫等你来发现，免费在线下拉式阅读，随时随地畅享精彩故事
        </p>
        <Link
          to="/category/all"
          className="whitespace-nowrap inline-flex items-center gap-2 px-8 py-3.5 rounded-full bg-primary-500 text-white font-medium text-sm hover:bg-primary-600 transition-all cursor-pointer"
        >
          立即探索 <i className="ri-arrow-right-line"></i>
        </Link>
      </div>
    </section>
  );
}

export default function Home() {
  return (
    <main className="min-h-screen bg-background-50">
      {/* JSON-LD: WebPage + BreadcrumbList */}
      <script type="application/ld+json" dangerouslySetInnerHTML={{__html: JSON.stringify({
        "@context": "https://schema.org",
        "@type": "WebPage",
        "name": "韩漫漫画 - 免费韩漫在线下拉式阅读",
        "url": "https://www.example.com/",
        "description": "韩漫漫画网站提供免费韩漫在线下拉式阅读体验，无删减韩漫完整呈现，画质清晰流畅。",
        "breadcrumb": {
          "@type": "BreadcrumbList",
          "itemListElement": [
            {
              "@type": "ListItem",
              "position": 1,
              "name": "首页",
              "item": "https://www.example.com/"
            }
          ]
        },
        "isPartOf": {
          "@type": "WebSite",
          "name": "韩漫漫画",
          "url": "https://www.example.com/"
        }
      })}} />
      <HeroSection />
      <FeaturedSection />
      <CategoriesSection />
      <LatestAndRankingSection />
      <StatsSection />
      <CTASection />
      <Footer />
    </main>
  );
}